// Qué "clima" pinta el encabezado del Inicio: el cielo según la hora y los
// efectos animados según lo que mide la estación (y el pronóstico de la
// zona para lo que no mide, como las nubes). Todo sutil: es ambientación,
// no un dato — el dato está en las tarjetas.

export type SkyPhase = 'night' | 'dawn' | 'day' | 'dusk';

export interface SkyMood {
  phase: SkyPhase;
  // 0 despejado · 1 algo nublado · 2 cubierto
  clouds: 0 | 1 | 2;
  rain: 'none' | 'light' | 'heavy';
  wind: 'none' | 'breeze' | 'strong';
  // Resolana: solo de día, sin lluvia ni cielo cubierto; 'hot' con calor.
  sun: 'none' | 'soft' | 'hot';
  stars: boolean;
}

export interface SkyInputs {
  // Hora local del campo (0–24, con decimales).
  hour: number;
  temperature: number | null;
  windKmh: number | null;
  // mm en la última hora (pluviómetro); null = la estación no tiene.
  rainLastHour: number | null;
  // Código WMO del pronóstico para esta hora, si hay.
  weatherCode: number | null;
}

export const BREEZE_KMH = 15;
export const STRONG_WIND_KMH = 30;
export const HOT_C = 28;

export function skyPhase(hour: number): SkyPhase {
  if (hour < 6 || hour >= 21) return 'night';
  if (hour < 8) return 'dawn';
  if (hour >= 18.5) return 'dusk';
  return 'day';
}

const isRainCode = (code: number) => (code >= 51 && code <= 67) || (code >= 80 && code <= 99);

export function skyMood(i: SkyInputs): SkyMood {
  const phase = skyPhase(i.hour);
  const code = i.weatherCode;

  // La lluvia medida manda; si la estación no tiene pluviómetro, el pronóstico.
  let rain: SkyMood['rain'] = 'none';
  if (i.rainLastHour !== null && i.rainLastHour > 0) rain = i.rainLastHour >= 4 ? 'heavy' : 'light';
  else if (i.rainLastHour === null && code !== null && isRainCode(code)) rain = code >= 63 && code !== 80 ? 'heavy' : 'light';

  let clouds: SkyMood['clouds'] = 0;
  if (rain !== 'none' || (code !== null && code >= 3)) clouds = 2;
  else if (code !== null && code >= 1) clouds = 1;

  const wind: SkyMood['wind'] =
    i.windKmh === null ? 'none' : i.windKmh >= STRONG_WIND_KMH ? 'strong' : i.windKmh >= BREEZE_KMH ? 'breeze' : 'none';

  const sunny = (phase === 'day' || phase === 'dusk' || phase === 'dawn') && rain === 'none' && clouds < 2;
  const sun: SkyMood['sun'] = !sunny ? 'none' : phase === 'day' && i.temperature !== null && i.temperature >= HOT_C ? 'hot' : 'soft';

  return {
    phase,
    clouds,
    rain,
    wind,
    sun,
    stars: phase === 'night' && clouds < 2,
  };
}

// Hora local en Argentina (el campo), sin depender de la zona del navegador.
export function fieldHour(now = new Date()): number {
  const h = now.getUTCHours() - 3 + now.getUTCMinutes() / 60;
  return (h + 24) % 24;
}
